import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Accordion,
  AccordionItem,
  type Column,
  Details,
  EmptyStation,
  type FormActionData,
  InfoPanel,
  type InitialFormData,
  List,
  MessageBar,
  type Nullable,
  Paragraph,
  Section,
  SingleLineTextField,
  getFormDiff,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field } from 'formik';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  DeleteDeploymentManifestDocument,
  type DeleteDeploymentManifestMutation,
  type DeleteDeploymentManifestMutationVariables,
  DeploymentManifestDocument,
  type DeploymentManifestQuery,
  UpdateDeploymentManifestDocument,
  type UpdateDeploymentManifestMutation,
  type UpdateDeploymentManifestMutationVariables,
} from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import classes from './DeploymentManifestDetails.module.scss';

type DeploymentManifest = NonNullable<
  DeploymentManifestQuery['serviceDeploymentManifest']
>;

type FormData = Nullable<DeploymentManifest>;

export const DeploymentManifestDetails: React.FC = () => {
  const { envId, customizableServiceId, deploymentManifestId } = useParams<{
    envId: string;
    customizableServiceId: string;
    deploymentManifestId: string;
  }>();

  // Query messaging setup
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  const [deploymentManifestDetails, setDeploymentManifestDetails] =
    useState<DeploymentManifest | null>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of hosting-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          const apolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );
          const result = await apolloClient.query<DeploymentManifestQuery>({
            query: DeploymentManifestDocument,
            variables: {
              id: deploymentManifestId,
            },
            fetchPolicy: 'network-only',
          });

          if (result.errors) {
            setError(result.errors[0].message);
          } else {
            if (result.data) {
              setDeploymentManifestDetails(
                result.data.serviceDeploymentManifest,
              );
            }
          }
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, customizableServiceId, deploymentManifestId]);

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: InitialFormData<FormData>,
    ): Promise<void> => {
      const apolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      await apolloClient.mutate<
        UpdateDeploymentManifestMutation,
        UpdateDeploymentManifestMutationVariables
      >({
        mutation: UpdateDeploymentManifestDocument,
        variables: {
          input: {
            id: deploymentManifestId,
            patch: getFormDiff(formData, initialData.data),
          },
        },
      });
    },
    [envId, deploymentManifestId],
  );

  const Form: React.FC = () => {
    // ConfigMap Variable List
    interface Variables {
      key: string;
      value: string;
    }
    const variableColumns: Column<Variables>[] = [
      {
        propertyName: 'key',
        label: 'Key',
        sortable: false,
      },
      {
        propertyName: 'value',
        label: 'Value',
        sortable: false,
      },
    ];

    const regularVariables: Variables[] = [];

    if (deploymentManifestDetails?.configMap.regularVariables !== undefined) {
      for (const key in deploymentManifestDetails?.configMap.regularVariables) {
        const regularVariable = {
          key: `${key}`,
          value: `${deploymentManifestDetails?.configMap.regularVariables[key]}`,
        };
        regularVariables.push(regularVariable);
      }
      regularVariables.sort((a, b) => a.key.localeCompare(b.key));
    }

    const secureVariables: Variables[] = [];

    if (deploymentManifestDetails?.configMap.secureVariables !== undefined) {
      for (const key in deploymentManifestDetails?.configMap.secureVariables) {
        const secureVariable = {
          key: `${key}`,
          value: `${deploymentManifestDetails?.configMap.secureVariables[key]}`,
        };
        secureVariables.push(secureVariable);
      }
      secureVariables.sort((a, b) => a.key.localeCompare(b.key));
    }

    // ConfigMap Service Account List
    interface ListServiceAccountsData {
      name: string;
      serviceId: string;
      permission: string;
    }
    const serviceAccountsColumns: Column<ListServiceAccountsData>[] = [
      {
        propertyName: 'name',
        label: 'Name',
        sortable: false,
        size: '20%',
      },
      {
        propertyName: 'serviceId',
        label: 'Service Id',
        sortable: false,
        size: '40%',
      },
      {
        propertyName: 'permission',
        label: 'Permission',
        sortable: false,
        size: '40%',
      },
    ];

    const serviceAccountData: ListServiceAccountsData[] = [];

    if (deploymentManifestDetails?.configMap.serviceAccounts !== undefined) {
      for (const serviceAccount of deploymentManifestDetails?.configMap
        .serviceAccounts ?? []) {
        for (const permissionService of serviceAccount.permissionStructure ??
          []) {
          for (const permission of permissionService.permissions ?? []) {
            const permissionData = {
              name: `${serviceAccount.name}`,
              serviceId: `${permissionService.serviceId}`,
              permission: `${permission}`,
            };
            serviceAccountData.push(permissionData);
          }
        }
      }

      serviceAccountData.sort((a, b) => {
        const nameComparison = a.name.localeCompare(b.name);
        if (nameComparison !== 0) {
          return nameComparison;
        }
        return a.serviceId.localeCompare(b.serviceId);
      });
    }

    // ConfigMap Pilet Data List
    interface ListPiletData {
      name: string;
      key: string;
      value: string;
    }
    const piletsColumns: Column<ListPiletData>[] = [
      {
        propertyName: 'name',
        label: 'Name',
        sortable: false,
        size: '20%',
      },
      {
        propertyName: 'key',
        label: 'Key',
        sortable: false,
        size: '40%',
      },
      {
        propertyName: 'value',
        label: 'Value',
        sortable: false,
        size: '40%',
      },
    ];

    const piletsData: ListPiletData[] = [];

    if (deploymentManifestDetails?.configMap.pilets !== undefined) {
      for (const pilets of deploymentManifestDetails?.configMap.pilets ?? []) {
        for (const key in pilets.args) {
          const pilet = {
            name: `${pilets.name}`,
            key: `${key}`,
            value: `${pilets.args[key]}`,
          };

          piletsData.push(pilet);
        }
      }
      piletsData.sort((a, b) => {
        const nameComparison = a.name.localeCompare(b.name);
        if (nameComparison !== 0) {
          return nameComparison;
        }
        return a.key.localeCompare(b.key);
      });
    }

    // ConfigMap DNS Mapped ports List
    interface ListDNSData {
      name: string;
      port: string;
    }
    const dnsMappedPortsColumns: Column<ListDNSData>[] = [
      {
        propertyName: 'name',
        label: 'Name',
        sortable: false,
      },
      {
        propertyName: 'port',
        label: 'Port',
        sortable: false,
      },
    ];

    const dnsMappedPortsData: ListDNSData[] = [];

    if (deploymentManifestDetails?.configMap.dnsMappedPorts !== undefined) {
      for (const dnsMappedPorts of deploymentManifestDetails?.configMap
        .dnsMappedPorts ?? []) {
        const dnsMappedPort = {
          name: `${dnsMappedPorts.name}`,
          port: `${dnsMappedPorts.port}`,
        };
        dnsMappedPortsData.push(dnsMappedPort);
      }
      dnsMappedPortsData.sort((a, b) => a.name.localeCompare(b.name));
    }

    return (
      <>
        {deploymentManifestDetails?.name && (
          <Field name="name" label="Name" as={SingleLineTextField} />
        )}

        <Accordion
          header={
            <div className={clsx(classes.row, classes.header)}>
              <span>All Sections</span>
            </div>
          }
          stickyRows={true}
        >
          {deploymentManifestDetails?.configMap?.regularVariables && (
            <AccordionItem
              header={
                <div className={clsx(classes.row)}>
                  <span>Regular Variables</span>
                </div>
              }
            >
              <div className={clsx(classes.content)}>
                <List
                  columns={variableColumns}
                  data={regularVariables}
                  showActionButton={false}
                />
              </div>
            </AccordionItem>
          )}

          {deploymentManifestDetails?.configMap?.regularVariables && (
            <AccordionItem
              header={
                <div className={clsx(classes.row)}>
                  <span>Secure Variables</span>
                </div>
              }
            >
              <div className={clsx(classes.content)}>
                <List
                  columns={variableColumns}
                  data={secureVariables}
                  showActionButton={false}
                />
              </div>
            </AccordionItem>
          )}

          {deploymentManifestDetails?.configMap?.serviceAccounts && (
            <AccordionItem
              header={
                <div className={clsx(classes.row)}>
                  <span>Service Accounts</span>
                </div>
              }
            >
              <div className={clsx(classes.content)}>
                <List
                  columns={serviceAccountsColumns}
                  data={serviceAccountData}
                  showActionButton={false}
                />
              </div>
            </AccordionItem>
          )}

          {deploymentManifestDetails?.configMap?.pilets && (
            <AccordionItem
              header={
                <div className={clsx(classes.row)}>
                  <span>Pilet Data</span>
                </div>
              }
            >
              <div className={clsx(classes.content)}>
                <List
                  columns={piletsColumns}
                  data={piletsData}
                  showActionButton={false}
                />
              </div>
            </AccordionItem>
          )}

          {deploymentManifestDetails?.configMap?.dnsMappedPorts && (
            <AccordionItem
              header={
                <div className={clsx(classes.row)}>
                  <span>DNS Mapped Ports</span>
                </div>
              }
            >
              <div className={clsx(classes.content)}>
                <List
                  columns={dnsMappedPortsColumns}
                  data={dnsMappedPortsData}
                  showActionButton={false}
                  defaultSortOrder={{
                    column: 'name',
                    direction: 'desc',
                  }}
                />
              </div>
            </AccordionItem>
          )}
        </Accordion>
      </>
    );
  };

  const { actions } = useActions(
    envId,
    customizableServiceId,
    deploymentManifestId,
  );
  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Deployment Manifest Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="Deployment Manifest Details"
          actions={actions}
          initialData={{
            data: deploymentManifestDetails,
            loading,
            error,
          }}
          saveData={onSubmit}
          infoPanel={
            deploymentManifestDetails ? (
              <Panel deploymentManifestDetails={deploymentManifestDetails} />
            ) : undefined
          }
        >
          <Form />
        </Details>
      )}
    </>
  );
};

const Panel: React.FC<{
  deploymentManifestDetails: DeploymentManifest;
}> = ({ deploymentManifestDetails }) => {
  return (
    <InfoPanel>
      <Section>
        <>
          <Paragraph title="ID">
            {deploymentManifestDetails?.id ?? ''}
          </Paragraph>
          <Paragraph title="Service ID">
            {deploymentManifestDetails?.serviceId ?? ''}
          </Paragraph>
          <Paragraph title="Service Name">
            {deploymentManifestDetails?.serviceDefinition?.name ?? ''}
          </Paragraph>
          <Paragraph title="Service Definition ID">
            {deploymentManifestDetails?.serviceDefinitionId ?? ''}
          </Paragraph>
          <Paragraph title="Schema Version">
            {deploymentManifestDetails?.configMap.version ?? ''}
          </Paragraph>
          <Paragraph title="Owned by Axinom">
            {deploymentManifestDetails.ownedByAxinom === true ? 'Yes' : 'No'}
          </Paragraph>
          <Paragraph title="Created">
            {deploymentManifestDetails
              ? new Date(
                  deploymentManifestDetails.createdDate,
                ).toLocaleString() +
                ' by ' +
                deploymentManifestDetails.createdUser
              : ''}
          </Paragraph>
        </>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">
          {deploymentManifestDetails.tenantId}
        </Paragraph>
        <Paragraph title="Environment ID">
          {deploymentManifestDetails?.environmentId}
        </Paragraph>
      </Section>
    </InfoPanel>
  );
};

function useActions(
  envId: string,
  customizableServiceId: string,
  serviceDeploymentId: string,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const history = useHistory();

  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
  );

  const actions: FormActionData<FormData>[] = [];

  actions.push({
    label: 'Delete',
    confirmationMode: 'Advanced',
    confirmationConfig: {
      body: 'The action is irreversible. By confirming the action the current service deployment manifests is going to be deleted.',
    },
    onActionSelected: async () => {
      await envApolloClient.mutate<
        DeleteDeploymentManifestMutation,
        DeleteDeploymentManifestMutationVariables
      >({
        mutation: DeleteDeploymentManifestDocument,
        variables: {
          input: {
            id: serviceDeploymentId,
          },
        },
        fetchPolicy: 'no-cache',
      });
      history.push(
        `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployment-manifests`,
      );
    },
  });

  return { actions } as const;
}

export const DeploymentManifestDetailsCrumb: BreadcrumbResolver = () =>
  'Deployment Manifest Details';
